/**
 * NetworkSection Component
 * ========================
 * Global network map with info panel
 * ALL content from Blueprint - NO hardcoded text
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useBlueprint, BlueprintSection } from '../../context/BlueprintContext';
import { ArrowRight, MapPin, Layers, Sparkles } from 'lucide-react';

const NetworkSection = () => {
  const { homepage, getRoute } = useBlueprint();
  const network = homepage?.network || {};
  const panel = network?.panel || {};
  
  // Get network link from config
  const networkLink = network.linkUrl || getRoute('explorer', '/materials');
  
  // Parse title with newlines
  const renderTitle = () => {
    const title = network.title || '';
    return title.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < title.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };
  
  return (
    <BlueprintSection name="network">
      <section className="bg-[var(--dark-bg)] py-24 relative overflow-hidden">
        {/* Glow */}
        <div className="absolute top-[15%] left-1/2 w-[700px] h-[500px] bg-[radial-gradient(ellipse,rgba(169,136,101,0.06)_0%,transparent_70%)] pointer-events-none" />
        
        <div className="sr-container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.8fr] gap-16 items-center">
            {/* Left - Text */}
            <div>
              <p className="sr-label-gold mb-6">
                {network.label}
              </p>
              
              <h2 className="sr-title-section-white mb-6">
                {renderTitle()}
              </h2>
              
              <p className="sr-description-light mb-10 max-w-[380px]">
                {network.description}
              </p>
              
              <Link to={networkLink} className="sr-link-gold">
                {network.link}
                <ArrowRight size={14} />
              </Link>
            </div>
            
            {/* Right - Map */}
            <div className="relative rounded-[18px] border border-[#1E1E1C] overflow-hidden bg-gradient-to-br from-[#0E0E0C] via-[#080807] to-[#0A0A08] min-h-[380px]">
              {/* SVG Map */}
              <svg viewBox="0 0 1000 500" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full">
                {/* Grid */}
                <g opacity=".04" stroke="white" strokeWidth=".5">
                  {[0, 100, 200, 300, 400, 500, 600, 700, 800, 900].map(x => (
                    <line key={`v${x}`} x1={x} y1="0" x2={x} y2="500" />
                  ))}
                  {[0, 83, 167, 250, 333, 417].map(y => (
                    <line key={`h${y}`} x1="0" y1={y} x2="1000" y2={y} />
                  ))}
                </g>
                
                {/* Continents */}
                <g fill="rgba(169,136,101,.08)" stroke="rgba(169,136,101,.22)" strokeWidth=".8">
                  <path d="M 42,88 L 100,60 L 180,48 L 248,52 L 278,72 L 270,148 L 250,196 L 198,218 L 150,208 L 112,180 L 88,142 Z" />
                  <path d="M 268,228 L 310,220 L 370,238 L 402,248 L 388,318 L 348,370 L 308,408 L 270,392 L 245,338 L 250,268 Z" />
                  <path d="M 468,88 L 500,78 L 532,80 L 558,88 L 562,108 L 548,132 L 518,142 L 490,148 L 462,138 L 452,118 Z" />
                  <path d="M 452,152 L 548,148 L 618,158 L 648,202 L 638,278 L 618,322 L 572,368 L 520,372 L 462,338 L 438,268 L 442,202 Z" />
                  <path d="M 558,88 L 648,68 L 752,52 L 862,48 L 952,68 L 988,108 L 968,168 L 902,212 L 828,238 L 748,238 L 688,218 L 638,188 L 598,158 L 562,132 Z" />
                  <path d="M 694,258 L 722,218 L 738,252 L 718,278 Z" />
                  <path d="M 818,298 L 892,292 L 928,318 L 922,368 L 878,388 L 832,382 L 808,352 L 812,318 Z" />
                  <path d="M 258,28 L 318,18 L 348,28 L 338,58 L 298,68 L 262,58 Z" />
                </g>
                
                {/* Connection Lines */}
                <g stroke="#a98865" strokeWidth=".8" strokeDasharray="6,7" opacity=".35">
                  <line x1="534" y1="139" x2="370" y2="303" />
                  <line x1="534" y1="139" x2="717" y2="197" />
                  <line x1="534" y1="139" x2="231" y2="150" />
                  <line x1="534" y1="139" x2="589" y2="143" />
                  <line x1="370" y1="303" x2="717" y2="197" />
                  <line x1="717" y1="197" x2="793" y2="159" />
                  <line x1="717" y1="197" x2="578" y2="323" />
                  <line x1="793" y1="159" x2="886" y2="151" />
                  <line x1="534" y1="139" x2="886" y2="151" />
                </g>
                
                {/* Circles */}
                <circle cx="534" cy="139" r="18" fill="none" stroke="#a98865" strokeWidth=".6" opacity=".25" />
                <circle cx="370" cy="303" r="18" fill="none" stroke="#a98865" strokeWidth=".6" opacity=".25" />
                
                {/* Pulsing Nodes */}
                <circle className="sr-pulse sr-pulse-fast" cx="534" cy="139" r="5" fill="#a98865" style={{ filter: 'drop-shadow(0 0 8px #a98865)' }} />
                <circle className="sr-pulse sr-pulse-fast" cx="370" cy="303" r="5" fill="#a98865" style={{ filter: 'drop-shadow(0 0 8px #a98865)' }} />
                <circle className="sr-pulse" cx="717" cy="197" r="3.5" fill="#a98865" style={{ filter: 'drop-shadow(0 0 5px #a98865)', animationDelay: '.4s' }} />
                <circle className="sr-pulse" cx="793" cy="159" r="3.5" fill="#a98865" style={{ filter: 'drop-shadow(0 0 5px #a98865)', animationDelay: '.8s' }} />
                <circle className="sr-pulse" cx="231" cy="150" r="3.5" fill="#a98865" style={{ filter: 'drop-shadow(0 0 5px #a98865)', animationDelay: '1.2s' }} />
                <circle className="sr-pulse" cx="578" cy="323" r="3.5" fill="#a98865" style={{ filter: 'drop-shadow(0 0 5px #a98865)', animationDelay: '.6s' }} />
                <circle className="sr-pulse" cx="886" cy="151" r="3.5" fill="#a98865" style={{ filter: 'drop-shadow(0 0 5px #a98865)', animationDelay: '1s' }} />
                <circle className="sr-pulse" cx="589" cy="143" r="3.5" fill="#a98865" style={{ filter: 'drop-shadow(0 0 5px #a98865)', animationDelay: '.2s' }} />
              </svg>
              
              {/* Info Panel */}
              <div className="absolute top-1/2 right-[4%] -translate-y-1/2 bg-[rgba(10,10,9,0.96)] border border-[rgba(169,136,101,0.3)] rounded-[14px] p-6 min-w-[224px] backdrop-blur-[16px] shadow-[0_16px_48px_rgba(0,0,0,0.6)]">
                {/* Origin */}
                <div className="flex gap-3 items-start mb-5">
                  <div className="w-8 h-8 rounded-lg bg-[rgba(169,136,101,0.15)] flex items-center justify-center flex-shrink-0">
                    <MapPin size={15} className="text-[var(--gold)]" />
                  </div>
                  <div>
                    <div className="text-[9.5px] text-[#999] tracking-[0.5px] uppercase font-semibold mb-0.5">
                      {panel.origin?.label}
                    </div>
                    <div className="text-sm text-[#EEE] font-bold">
                      {panel.origin?.value}
                    </div>
                  </div>
                </div>
                
                {/* Availability */}
                <div className="flex gap-3 items-start mb-5">
                  <div className="w-8 h-8 rounded-lg bg-[rgba(169,136,101,0.15)] flex items-center justify-center flex-shrink-0">
                    <Layers size={15} className="text-[var(--gold)]" />
                  </div>
                  <div>
                    <div className="text-[9.5px] text-[#999] tracking-[0.5px] uppercase font-semibold mb-0.5">
                      {panel.availability?.label}
                    </div>
                    <div className="text-sm text-[#EEE] font-bold">
                      {panel.availability?.value}
                    </div>
                  </div>
                </div>
                
                {/* Finishes */}
                <div className="flex gap-3 items-start mb-5">
                  <div className="w-8 h-8 rounded-lg bg-[rgba(169,136,101,0.15)] flex items-center justify-center flex-shrink-0">
                    <Sparkles size={15} className="text-[var(--gold)]" />
                  </div>
                  <div>
                    <div className="text-[9.5px] text-[#999] tracking-[0.5px] uppercase font-semibold mb-0.5">
                      {panel.finishes?.label}
                    </div>
                    <div className="text-sm text-[#EEE] font-bold">
                      {panel.finishes?.value}
                    </div>
                  </div>
                </div>
                
                {/* CTA */}
                <button className="sr-btn-gold">
                  {panel.cta}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </BlueprintSection>
  );
};

export default NetworkSection;
